import { useRef, useState, type DragEvent } from "react";
import { CheckCircle2, Loader2, UploadCloud, X } from "lucide-react";
import { request } from "../lib/api";
import type { PresignResponse, UploadPurpose } from "../types";
import { errorMessage } from "./ui";

const ACCEPT: Record<UploadPurpose, string> = {
  video: "video/mp4,video/webm,video/quicktime",
  audio: "audio/mpeg,audio/mp4,audio/wav,audio/ogg",
  image: "image/png,image/jpeg,image/webp,image/gif",
  subtitle: "text/vtt,.srt,text/plain",
  document: ".pdf,.doc,.docx,.xls",
  slides: ".pdf,.ppt,.pptx",
  resource: ".pdf,.zip,.doc,.docx,image/png,image/jpeg",
  thumbnail: "image/png,image/jpeg,image/webp",
};

const MAX_LABEL: Record<UploadPurpose, string> = {
  video: "500 MB",
  audio: "200 MB",
  image: "15 MB",
  subtitle: "2 MB",
  document: "50 MB",
  slides: "100 MB",
  resource: "100 MB",
  thumbnail: "5 MB",
};

interface UploadProps {
  purpose: UploadPurpose;
  label: string;
  value?: string | null;
  fileName?: string;
  onUploaded: (publicUrl: string, fileName: string, fileSize: number, mimeType: string) => void;
  onRemove?: () => void;
}

// Upload is the one integrated file-picker/drag-drop widget used across all
// admin content forms: it presigns, PUTs directly to storage, and hands the
// resulting public URL straight to the form field — the admin never sees or
// copies a URL by hand.
export function Upload({ purpose, label, value, fileName, onUploaded, onRemove }: UploadProps) {
  const [state, setState] = useState<"idle" | "uploading" | "error">("idle");
  const [progressLabel, setProgressLabel] = useState("");
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function upload(file: File) {
    setState("uploading");
    setError("");
    setProgressLabel("Requesting upload URL…");
    try {
      const signed = await request<PresignResponse>("/api/admin/media/presign", {
        method: "POST",
        body: JSON.stringify({
          file_name: file.name,
          content_type: file.type || "application/octet-stream",
          purpose,
          file_size: file.size,
        }),
      });
      setProgressLabel("Uploading…");
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", signed.upload_url);
        if (file.type) xhr.setRequestHeader("Content-Type", file.type);
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setProgressLabel(`Uploading… ${Math.round((event.loaded / event.total) * 100)}%`);
          }
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("Upload failed")));
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(file);
      });
      setState("idle");
      onUploaded(signed.public_url, file.name, file.size, file.type);
    } catch (cause) {
      setState("error");
      setError(errorMessage(cause));
    }
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void upload(file);
  }

  return (
    <div className="grid gap-2">
      <span className="text-sm font-medium text-content-primary">{label}</span>
      {value ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-muted px-3.5 py-2.5">
          <span className="flex min-w-0 items-center gap-2 text-sm text-content-primary">
            <CheckCircle2 size={16} className="shrink-0 text-success" />
            <span className="truncate">{fileName || value}</span>
          </span>
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              className="text-xs font-semibold text-interactive hover:underline"
              onClick={() => inputRef.current?.click()}
            >
              Replace
            </button>
            {onRemove && (
              <button type="button" className="text-content-secondary hover:text-danger" onClick={onRemove}>
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div
          className={`rounded-md border border-dashed p-5 text-center transition-colors ${
            dragOver ? "border-interactive bg-interactive/5" : "border-border-strong bg-surface-muted"
          }`}
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {state === "uploading" ? (
            <>
              <Loader2 size={22} className="mx-auto animate-spin text-interactive" />
              <p className="mt-2 text-sm font-medium text-content-primary">{progressLabel}</p>
            </>
          ) : (
            <>
              <UploadCloud size={22} className="mx-auto text-interactive" />
              <p className="mt-2 text-sm font-medium text-content-primary">
                Drag a file here or{" "}
                <button
                  type="button"
                  className="font-semibold text-interactive hover:underline"
                  onClick={() => inputRef.current?.click()}
                >
                  browse
                </button>
              </p>
              <p className="mt-1 text-xs text-content-secondary">Max size {MAX_LABEL[purpose]}</p>
            </>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={ACCEPT[purpose]}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void upload(file);
          event.target.value = "";
        }}
      />
      {state === "error" && <p className="text-sm text-danger">{error}</p>}
    </div>
  );
}
